import React, { forwardRef, HTMLAttributes, ReactNode } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { IOptionsProps } from '../../components/bootstrap/Option';
import ReactSelect from 'react-select';
import Validation from '../../components/bootstrap/forms/Validation';
import './select.css';
import useDarkMode from '../../hooks/useDarkMode';
import { customStyles, customStylesForCanvas } from '../../services/searchableSelect.service';

interface ISelectProps extends Partial<IOptionsProps>, HTMLAttributes<HTMLSelectElement> {
	id?: string;
	className?: string;
	name?: string;
	children?: ReactNode;
	ariaLabel: string;
	placeholder?: string;
	size?: 'lg' | 'sm';
	multiple?: boolean;
	disabled?: boolean;
	required?: boolean;
	isFloating?: boolean;
	ariaDescribedby?: string;
	ariaLabelledby?: string;
	title?: string;
	// value?: string | string[];
	value?: any | string[];
	defaultValue?: string | string[];
	isTouched?: boolean;
	isValid?: boolean;
	invalidFeedback?: string;
	validFeedback?: string;
	isValidMessage?: boolean;
	isTooltipFeedback?: boolean;
	/**
	 * When true, renders the dropdown menu in a portal attached to document.body.
	 * Useful inside scrollable/overflow-hidden containers (e.g., modals) to prevent clipping.
	 * Opt-in only so other screens aren't affected.
	 */
	portalMenuToBody?: boolean;
	/** z-index for the portaled menu (only when portalMenuToBody is true). */
	portalMenuZIndex?: number;
	onBlur?(...args: unknown[]): unknown;
	onChange?(...args: unknown[]): unknown;
	onFocus?(...args: unknown[]): unknown;
	onInput?(...args: unknown[]): unknown;
	onInvalid?(...args: unknown[]): unknown;
	onSelect?(...args: unknown[]): unknown;
	list?: any;
	noOptionsMessage?: any;
	formatOptionLabel?: (option: any, labelMeta: any) => React.ReactNode;
}

const customNoOptionsMessage = () => 'No Data available';

const SearchableSelect = forwardRef<HTMLSelectElement, ISelectProps>(
	(
		{
			id = undefined,
			name = undefined,
			className = undefined,
			children = null,
			ariaLabel = '',
			placeholder = undefined,
			size = undefined,
			multiple = false,
			disabled = false,
			required = false,
			isFloating = false,
			ariaDescribedby = undefined,
			ariaLabelledby = undefined,
			title = undefined,
			value = undefined,
			defaultValue = undefined,
			isTouched = false,
			isValid = false,
			invalidFeedback = undefined,
			validFeedback = undefined,
			isValidMessage = true,
			isTooltipFeedback = false,
			portalMenuToBody = false,
			portalMenuZIndex = 9999,
			onBlur = undefined,
			onChange = undefined,
			onFocus = undefined,
			onInput = undefined,
			onInvalid = undefined,
			onSelect = undefined,
			noOptionsMessage = undefined,
			formatOptionLabel = undefined,
			...props
		},
		ref,
	) => {
		const { darkModeStatus } = useDarkMode();
		const menuPortalTarget =
			portalMenuToBody && typeof window !== 'undefined' ? document.body : undefined;

		return (
			<>
				{isFloating ? (
					<ReactSelect
						id='selectSize'
						isClearable
						className={classNames(
							'form-select',
							{
								[`form-select-${size}`]: size,
								'text-muted': value === '' && placeholder,
								'is-invalid': !isValid && isTouched && invalidFeedback,
								'is-valid': !isValid && isTouched && !invalidFeedback,
							},
							className,
						)}
						placeholder={placeholder ? placeholder : ariaLabel}
						aria-label={ariaLabel}
						value={value}
						onChange={onChange}
						options={props.list}
						onBlur={onBlur}
						isMulti={multiple}
						styles={{
							...customStyles(darkModeStatus),
							...(portalMenuToBody
								? {
										menuPortal: (base: any) => ({ ...base, zIndex: portalMenuZIndex }),
									}
								: null),
						}}
						onFocus={onFocus}
						noOptionsMessage={noOptionsMessage || customNoOptionsMessage}
						isDisabled={disabled}
						menuPortalTarget={menuPortalTarget as any}
						menuPosition={portalMenuToBody ? 'fixed' : 'absolute'}
						menuShouldScrollIntoView={!portalMenuToBody}
						formatOptionLabel={formatOptionLabel}
					/>
				) : (
					<ReactSelect
						id='selectSizeForCanvas'
						isClearable
						className={classNames(
							'form-select',
							{
								[`form-select-${size}`]: size,
								'text-muted': value === '' && placeholder,
								'is-invalid': !isValid && isTouched && invalidFeedback,
								'is-valid': !isValid && isTouched && !invalidFeedback,
							},
							className,
						)}
						placeholder={placeholder ? placeholder : ariaLabel}
						aria-label={ariaLabel}
						value={value}
						onChange={onChange}
						options={props.list}
						onBlur={onBlur}
						styles={{
							...customStylesForCanvas(darkModeStatus),
							...(portalMenuToBody
								? {
										menuPortal: (base: any) => ({ ...base, zIndex: portalMenuZIndex }),
									}
								: null),
						}}
						onFocus={onFocus}
						isMulti={multiple}
						noOptionsMessage={noOptionsMessage || customNoOptionsMessage}
						isDisabled={disabled}
						menuPortalTarget={menuPortalTarget as any}
						menuPosition={portalMenuToBody ? 'fixed' : 'absolute'}
						menuShouldScrollIntoView={!portalMenuToBody}
						formatOptionLabel={formatOptionLabel}
					/>
				)}
				{isValidMessage && (
					<Validation
						isTouched={isTouched}
						invalidFeedback={invalidFeedback}
						validFeedback={validFeedback}
						isTooltip={isTooltipFeedback}
					/>
				)}
			</>
		);
	},
);

SearchableSelect.displayName = 'Select';

export default SearchableSelect;
